import { useFirebaseContext, useConfigurationContext } from '@medely/ui-kit';
import { IAccount } from '@medely/types';
import { GET_CURRENT_USER, isSaasAccount, isAgencyAccount } from '@mp';
import { QueryObserverResult, useQuery } from '@tanstack/react-query';
import useGraphQLRequest from './useGraphQLRequest';

// GET_CURRENT_USER queries `intercom_jwt`, but @medely/types' IAccount hasn't caught up yet.
declare module '@medely/types' {
  interface IAccount {
    intercom_jwt?: string;
  }
}

// TODO (INT-1219) - migrate everything to use the useCurrentUser hook from multi-platform and delete this file
const useCurrentUser = (): {
  isLoading: boolean;
  currentUser: Omit<IAccount, 'agency'> | undefined;
  isArchived: boolean;
  isDeactivated: boolean;
  isRejected: boolean;
  isApplicant: boolean;
  isSaas: boolean;
  isAgency: boolean;
  isW2Eligible: boolean;
  isW2Enabled: boolean;
  refetch: () => Promise<QueryObserverResult<IAccount, unknown>>;
  isDeletionRequested: boolean;
  proType: 'irp' | 'agency' | 'marketplace';
} => {
  const { googleUser, logout } = useFirebaseContext();
  const { adminUrl, facilityUrl } = useConfigurationContext();
  const { request } = useGraphQLRequest();
  const enabled = !!googleUser;

  const REDIRECT_URLS: Partial<Record<IAccount['account_type'], string>> = {
    administrator: adminUrl,
    facility: facilityUrl,
  };

  const getCurrentUser = async () => {
    const { me } = await request(GET_CURRENT_USER);
    return me;
  };

  const { isLoading, isInitialLoading, data, refetch } = useQuery(
    ['currentUser', googleUser?.email],
    getCurrentUser,
    {
      enabled,
      onSuccess: (account: IAccount) => {
        if (account.account_type !== 'professional') {
          logout?.(REDIRECT_URLS[account.account_type]);
        }
      },
    },
  );

  const isSaas = data ? isSaasAccount(data) : false;
  const isAgency = data ? isAgencyAccount(data) : false;
  const professional = data?.professional;
  const isDeletionRequested =
    !!professional &&
    'deactivation_reason' in professional &&
    professional.deactivation_reason === 'deletion_request';

  const getProType = () => {
    if (isSaas) {
      return 'irp';
    }
    if (isAgency) {
      return 'agency';
    }
    return 'marketplace';
  };

  return {
    isLoading: enabled ? isLoading : isInitialLoading,
    currentUser: data,
    isArchived: data?.professional?.application_status === 'archived',
    isDeactivated: data?.professional?.status === 'deactivated',
    isRejected: data?.professional?.application_status === 'rejected',
    isApplicant: data?.professional?.application_status !== 'approved',
    isSaas,
    isAgency,
    isW2Eligible: data?.professional?.w2_eligible ?? false,
    isW2Enabled: data?.professional?.w2_enabled ?? false,
    refetch,
    isDeletionRequested,
    proType: getProType(),
  };
};

export default useCurrentUser;
